import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ProductApiService } from '../../services/api/product-api.service';
import { CategoryService } from '../../../../core/services/category/category.service';
import { faBoxesStacked, faCheck, faPlus } from '@fortawesome/free-solid-svg-icons';
import { ModalService } from '../../../../core/services/modal/modal.service';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';
import { AddStockPromotionDialogComponent } from '../../components/add-stock-promotion-dialog/add-stock-promotion-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-product-list',
  templateUrl: './product-list.component.html',
  styleUrl: './product-list.component.css'
})
export class ProductListComponent implements OnInit {

  faCheck = faCheck;
  faPlus = faPlus;
  faBoxesStacked = faBoxesStacked;

  form: FormGroup;
  loading: boolean = true;
  branchMode: boolean;

  categories: Array<{ id: string; name: string; }> = this.categoryService?.categories || []
  products: Array<{ sku: string; name: string; category: Object; in_stock: number; prices: Object; }> = []
  priceList: Array<{ id: string; sku: string; }> = []
  datasets: any[] = []
  // stockPlatform: any[] = []

  countDataSource = 0;
  pageSize = 10;
  pageIndex = 0;
  limit = 10;
  offset = 0;

  channels: any[] = [];

  channelSelected: string = '';
  branchKeyword: string = '';
  branchesFilter: any[] = [];
  showBranchFilter: boolean = false;

  constructor(
    private fb: FormBuilder,
    private productApiService: ProductApiService,
    private categoryService: CategoryService,
    private modalService: ModalService,
    private authService: AuthService,
    private platformService: PlatformService,
    public dialog: MatDialog,
    router: Router,
  ) {
    this.branchMode = router.url.startsWith('/branch/');
    this.form = this.fb.group({
      keyword: [''],
      category_id: [''],
      min_stock: [null],
      max_stock: [null]
    });

  }


  ngOnInit(): void {
    const all = this.platformService?.channels || [];
    this.channels = all.filter(c => !!c.is_branch === this.branchMode);
    if (this.branchMode) {
      this.branchesFilter = this.channels;
      if (this.channels.length > 0) {
        this.channelSelected = this.channels[0].shop_ref;
        this.branchKeyword = this.channels[0].platform_name;
      }
    }
    this.getDataProduct()
  }

  filterBranches() {
    const k = (this.branchKeyword || '').toLowerCase();
    this.branchesFilter = this.channels.filter(c =>
      (c.platform_name || '').toLowerCase().includes(k)
    );
    this.showBranchFilter = true;
  }

  selectBranch(branch: any) {
    this.channelSelected = branch.shop_ref;
    this.branchKeyword = branch.platform_name;
    this.branchesFilter = this.channels;
    this.showBranchFilter = false;
  }

  openBranchFilter() {
    this.branchesFilter = this.channels;
    this.showBranchFilter = true;
  }

  closeBranchFilter() {
    setTimeout(() => {
      this.showBranchFilter = false;
      const current = this.channels.find(c => c.shop_ref === this.channelSelected);
      if (current) this.branchKeyword = current.platform_name;
    }, 150);
  }

  getDataProduct() {
    if (this.branchMode) {
      this.getBranchProducts()
    } else {
      this.getHqProducts()
    }
  }

  private getHqProducts() {
    this.loading = true;
    const value = this.form.getRawValue()
    const cleanFilter = Object.fromEntries(
      Object.entries(value).filter(([key, value]) => value !== null && value !== '')
    );
    const filters = {
      ...cleanFilter,
      fields: 'sku,name,category,in_stock,prices',
      limit: this.limit,
      offset: this.offset
    }

    this.productApiService.getAllProduct(filters).then(async (res: any) => {
      if (res.data) {
        this.products = res.data;
        this.countDataSource = res.count;
        const uniqueSku = res.data.map((e: { sku: any; }) => e.sku)
        if (uniqueSku.length > 0) {
          this.priceList = await this.searchPriceList(uniqueSku)
        }
      } else {
        this.products = [];
        this.countDataSource = 0;
      }
      this.reformattingDataset()
      this.loading = false;
    })
  }

  private async getBranchProducts() {
    this.loading = true;
    this.products = [];
    this.priceList = [];
    if (!this.channelSelected) {
      this.countDataSource = 0;
      this.reformattingDataset();
      this.loading = false;
      return;
    }

    const { keyword, category_id } = this.form.getRawValue();
    const filters: any = { channel: this.channelSelected, limit: this.limit, offset: this.offset };
    if (keyword) filters.keyword = keyword;
    if (category_id) filters.category_id = category_id;

    try {
      const channelRes: any = await this.productApiService.findProductChannel(filters);
      if (!channelRes?.success) {
        this.countDataSource = 0;
        this.reformattingDataset();
        this.loading = false;
        return;
      }
      this.countDataSource = channelRes.count || 0;
      const skus = (channelRes.data || []).map((e: any) => e.sku);
      if (skus.length === 0) {
        this.reformattingDataset();
        this.loading = false;
        return;
      }

      const branch = this.channels.find(c => c.shop_ref === this.channelSelected);
      const branchNumber = branch?.branch_number;
      const [productRes, stockRes]: any[] = await Promise.all([
        this.productApiService.getAllProduct({
          sku: skus.join(),
          fields: 'sku,name,category,in_stock',
          limit: skus.length
        }),
        branchNumber
          ? this.productApiService.findStockSabuysoft({ sku: skus.join(), branch: branchNumber })
          : Promise.resolve({ success: true, data: [] }),
      ]);
      const stockMap = new Map<string, number>(
        (stockRes?.data || []).map((e: any) => [e.sku, e.summary])
      );
      this.products = (productRes?.data || []).map((p: any) => ({
        ...p,
        branch_stock: stockMap.get(p.sku) ?? 0,
      }));
    } catch {
      this.products = [];
      this.countDataSource = 0;
    } finally {
      this.reformattingDataset();
      this.loading = false;
    }
  }

  async searchPriceList(sku: string[]) {
    const filters: any = { sku: sku.join(), is_last: true, fields: 'id,sku' }
    return await this.productApiService.searchPriceList(filters).then((res: any) => {
      if (res.success) {
        return res.data;
      }
    })
  }

  async getAllProductPlatform(sku: string[]) {
    const filters: any = { sku: sku.join(), fields: 'sku,channel,sellable' }
    return await this.productApiService.getAllProductPlatform(filters).then((res: any) => {
      if (res.success) {
        return res.data;
      }
    })
  }

  reformattingDataset() {
    const uniquePriceListSku = this.priceList.map(e => e.sku);
    this.datasets = this.products.map(e => ({ ...e, is_price_list: uniquePriceListSku.includes(e.sku) }));
  }

  submitForm() {
    this.offset = 0;
    this.pageIndex = 0;
    this.getDataProduct()
  }

  callBackPageChange = (data: any): void => {
    if (this.offset !== data.offset || this.limit !== data.limit) {
      this.offset = data.offset;
      this.limit = data.limit;
      this.getDataProduct()
    }

    if (data.pageIndex !== undefined) {
      this.pageIndex = data.pageIndex;
    }
    if (data.limit !== undefined) {
      this.pageSize = data.limit;
    }
  }

  addStockPromotion(data: any) {
    const dialogRef = this.dialog.open(AddStockPromotionDialogComponent, {
      data: data,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
    })
  }

  addProductPriceList(sku: string) {
    if (!this.canAccess()) {
      this.modalService.openModal('info', `ขออภัย`, 'สิทธิ์ของคุณไม่สามารถดำเนินการได้')
      return;
    }

    const data = { skus: [sku] }
    this.productApiService.createPriceList(data).then((res: any) => {
      if (res.success) {
        const index = this.datasets.findIndex(e => e.sku === sku)
        if (index !== -1) {
          this.datasets[index].is_price_list = true;
        }
      } else {
        this.modalService.openModal('fail', 'เกิดข้อผิดพลาด', 'ไม่สามารถเพิ่มสินค้าใน Price List ได้ในขณะนี้')
      }
    })
  }

  canAccess() {
    return this.authService.user?.role ? ['admin', 'marketing'].includes(this.authService.user.role) : false;
  }

  // getStock(channel_id: string, sku: string) {
  //   return this.stockPlatform.find(e => e.channel === channel_id && e.sku === sku)?.sellable
  // }

}
